import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SAVE_SEARCH_POPUP_SHOWN } from '../../constants/cookies';
import { getAmountScrolled, handleResize } from '../../utils/scrollHelper';
import './styles.css';

const SidePopUp = ({ children, listingId, hideSavedSearchPopup, tracking, portal }) => {
  const [cookies, setCookie] = useCookies([SAVE_SEARCH_POPUP_SHOWN]);
  const [trackLength, setTrackLength] = useState(0);
  const today = Math.floor(Date.now() / 1000);

  const hasBeenShown = () => {
    const popUpCookieValue = cookies[SAVE_SEARCH_POPUP_SHOWN]?.[listingId]?.closed;
    return !!popUpCookieValue || (popUpCookieValue + 86400) > today;
  };

  const initialState = hideSavedSearchPopup || hasBeenShown();
  const [closed, setClosed] = useState(initialState);
  const [hide, setHide] = useState(initialState);
  const [amIVisible, setAmIVisible] = useState(initialState);

  const cssClosed = closed ? 'side-popup__closed' : '';
  const cssHide = hide ? 'side-popup-container__hide' : '';

  let throttle;

  const handleCloseButton = () => {
    tracking.setGenericEvent('search alerts', 'create search', portal, '', 'SaveSearchPopupClosed');
    const savedSearchCookie = cookies[SAVE_SEARCH_POPUP_SHOWN];
    const inOneDay = new Date();
    inOneDay.setDate(inOneDay.getDate() + 1);

    setCookie(
      SAVE_SEARCH_POPUP_SHOWN,
      {
        ...savedSearchCookie,
        [listingId]: {
          closed: today
        }
      },
      {
        expires: inOneDay,
        path: '/',
        value: 1
      }
    );
    setClosed(true);
  };

  const handleScroll = () => {
    clearTimeout(throttle);
    // throttle code inside scroll to once every 25 milliseconds
    // this is used to prevent performance degradation
    throttle = setTimeout(() => {
      /* istanbul ignore else */
      if (!hasBeenShown()) {
        const scrolled = getAmountScrolled(trackLength);
        if (scrolled >= 10) {
          // We don't want to add an event every time the scroll condition is met.
          if (!amIVisible) {
            setAmIVisible(true);
            tracking.setGenericEvent('search alerts', 'create search', portal, '', 'SaveSearchPopupShown');
          }
          setHide(false);
          setClosed(false);
        }
        if (scrolled < 10) {
          setAmIVisible(false);
          setClosed(true);
        }
      }
      // This was covered by wrongly defined tests,that failed randomly,
      // related with some weird YACHTWORLD_JAVA_BDP_COOKIE.
      // feel free to cover it.
      /* istanbul ignore else */
      else if (!closed) {
        setAmIVisible(false);
        setClosed(true);
      }
    }, 25);
  };

  const handleHide = () => {
    if (closed) {
      setHide(true);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', setTrackLength(handleResize()));

    // cache initial viewport
    setTrackLength(handleResize());

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', setTrackLength(handleResize()));
    };
  });

  useEffect(() => {
    if (!hasBeenShown()) {
      setClosed(hideSavedSearchPopup);
      setHide(hideSavedSearchPopup);
    }
  }, [hideSavedSearchPopup]);

  return (
    <div className={classNames('side-popup-container', cssHide, 'should-hide-save-search')} id="side-popup-container">
      <div
        className={classNames('side-popup', cssClosed)}
        id="side-popup"
        onTransitionEnd={handleHide}>
        <button className="closeButton" onClick={() => handleCloseButton()}>
          <title>Close</title>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.2929 1.41L8.05645 6.64645L7.70289 7L8.05645 7.35355L13.2929 12.59L12.59 13.2929L7.35355 8.05645L7 7.70289L6.64645 8.05645L1.41 13.2929L0.707107 12.59L5.94355 7.35355L6.29711 7L5.94355 6.64645L0.707107 1.41L1.41 0.707107L6.64645 5.94355L7 6.29711L7.35355 5.94355L12.59 0.707107L13.2929 1.41Z" fill="white" stroke="#2C272D" />
          </svg>
        </button>
        {children}
      </div>
    </div>
  );
};

SidePopUp.defaultProps = {
  isLoading: false,
  listingId: null,
  hideSavedSearchPopup: true,
  portal: 'default'
};

SidePopUp.propTypes = {
  /** id */
  listingId: PropTypes.number,
  /** Flag for loading */
  isLoading: PropTypes.bool,
  /** Analytics tracking helper */
  tracking: PropTypes.shape({
    setGenericEvent: PropTypes.func.isRequired,
  }).isRequired,
  /** Forces the popup to hide/unhide */
  hideSavedSearchPopup: PropTypes.bool,
  children: PropTypes.node,
  portal: PropTypes.string
};

export default SidePopUp;
